import React from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Megaphone, Plus } from "lucide-react";
import { selectUser, selectUserProfile } from "src/store/authSlice";
import { canManageService, canReadService, ServiceDomain } from "src/lib/roles";
import { actorFromAuth } from "src/lib/activityService";
import { historyItemToFormPrefill } from "src/lib/broadcastService";
import type { NotificationHistoryListItem } from "src/types/broadcasts";
import BroadcastHistory from "src/components/broadcasts/BroadcastHistory";
import { SlabButton } from "src/components/ui/SlabButton";
import { Constants } from "../constant";

const BroadcastsPanel: React.FC = () => {
  const user = useSelector(selectUser);
  const userProfile = useSelector(selectUserProfile);
  const navigate = useNavigate();

  const canManage =
    userProfile != null &&
    canManageService(userProfile.role, ServiceDomain.Broadcasts);
  const actor = actorFromAuth(user, userProfile);

  const handleDuplicate = (item: NotificationHistoryListItem) => {
    // Duplicate always creates a NEW row — never reuses the source's draftId,
    // even when duplicating a draft.
    navigate(`${Constants.BROADCASTS_ROUTE}/create`, {
      state: { prefill: historyItemToFormPrefill(item) },
    });
  };

  const handleEdit = (item: NotificationHistoryListItem) => {
    // Only drafts are editable in place — the panel guards the button, but
    // double-check here so a stale click can't corrupt a published row.
    if (item.status !== "draft") return;
    navigate(`${Constants.BROADCASTS_ROUTE}/create`, {
      state: { prefill: historyItemToFormPrefill(item), draftId: item.id },
    });
  };

  if (
    !userProfile ||
    !canReadService(userProfile.role, ServiceDomain.Broadcasts)
  ) {
    return (
      <div className="py-6 sm:py-8">
        <h1 className="mb-3 text-2xl font-bold sm:mb-4 sm:text-4xl">
          Access Denied
        </h1>
        <p className="text-sm text-gray-400 sm:text-base">
          You don&apos;t have permission to view broadcasts.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl py-2 sm:py-4">
      <header className="mb-6 flex flex-col gap-4 sm:mb-8 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <h1 className="mb-2 flex items-center gap-2 text-2xl font-bol sm:gap-3 sm:text-4xl">
            <Megaphone className="h-7 w-7 shrink-0 text-teal-500 sm:h-8 sm:w-8" />
            {Constants.BROADCASTS_TITLE}
          </h1>
          <p className="max-w-2xl text-sm text-[#cfd1d7] sm:text-base">
            {Constants.BROADCASTS_DESCRIPTION}
          </p>
        </div>
        {canManage && (
          <SlabButton
            variant="primary"
            onClick={() => navigate(`${Constants.BROADCASTS_ROUTE}/create`)}
            className="w-full sm:w-auto"
          >
            <Plus className="h-4 w-4" aria-hidden /> New broadcast
          </SlabButton>
        )}
      </header>

      <BroadcastHistory
        canManage={canManage}
        actor={actor}
        onDuplicate={handleDuplicate}
        onEdit={handleEdit}
      />
    </div>
  );
};

export default BroadcastsPanel;
